import { useEffect, useRef, useState } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Microphone, Stop, Trash, Eye, EyeSlash, Waveform } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

/**
 * End-of-day voice debrief for a chantier.
 * Records webm audio, uploads to /api/chantiers/{id}/debrief (Whisper transcription),
 * then lists prior debriefs with transcription + audio player + delete/portal toggle.
 */
export default function DebriefRecorder({ chantierId }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "associate";
  const [debriefs, setDebriefs] = useState([]);
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [includeInPortal, setIncludeInPortal] = useState(true);
  const [elapsed, setElapsed] = useState(0);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const token = ""; // httpOnly cookie handles auth for <audio>/<img>; no localStorage read.

  const load = async () => {
    try { const r = await api.get(`/chantiers/${chantierId}/debriefs`); setDebriefs(r.data || []); }
    catch (err) { console.warn("debriefs load failed:", err); }
  };
  useEffect(() => { load(); return () => { if (timerRef.current) clearInterval(timerRef.current); }; }, [chantierId]);

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia) { toast.error("Micro indisponible"); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";
      const mr = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mime });
        await upload(blob);
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    } catch (err) {
      toast.error("Autorisation micro refusée");
    }
  };

  const stop = () => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") mediaRef.current.stop();
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setRecording(false);
  };

  const upload = async (blob) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("audio", blob, "debrief.webm");
      fd.append("language", "fr");
      fd.append("include_in_portal", includeInPortal ? "true" : "false");
      await api.post(`/chantiers/${chantierId}/debrief`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Debrief enregistré · transcription IA en cours");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); setElapsed(0); }
  };

  const togglePortal = async (d) => {
    try {
      await api.patch(`/chantiers/${chantierId}/debrief/${d.id}`, { include_in_portal: !d.include_in_portal });
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async (d) => {
    if (!window.confirm("Supprimer ce debrief ?")) return;
    try { await api.delete(`/chantiers/${chantierId}/debrief/${d.id}`); await load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");

  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-3 space-y-2.5" data-testid="debrief-recorder">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold flex items-center gap-1.5"><Waveform size={14} weight="duotone" className="text-[#005CFF]" /> Debrief fin de journée</div>
            <div className="text-[10px] text-white/50 mt-0.5">Dicte un résumé — la transcription IA rejoint le rapport client{" "}et le portail public si activé.</div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {recording ? (
          <Button onClick={stop} className="flex-1 bg-red-600 hover:bg-red-500 h-10 gap-2" data-testid="debrief-stop-btn">
            <Stop size={14} weight="fill" />
            <span className="font-mono text-sm">{mm}:{ss}</span>
            <span className="ml-1 text-xs opacity-80">Arrêter</span>
          </Button>
        ) : (
          <Button onClick={start} disabled={uploading} className="flex-1 bg-[#005CFF] hover:bg-[#0047CC] h-10 gap-2" data-testid="debrief-record-btn">
            {uploading ? (
              <><div className="w-3.5 h-3.5 border border-white/40 border-t-white rounded-full animate-spin" /><span className="text-xs">Transcription…</span></>
            ) : (
              <><Microphone size={14} weight="fill" /><span className="text-xs">Enregistrer un debrief</span></>
            )}
          </Button>
        )}
        <label className="flex items-center gap-1.5 text-[10px] text-white/60 shrink-0 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={includeInPortal}
            onChange={(e) => setIncludeInPortal(e.target.checked)}
            className="accent-[#005CFF]"
            data-testid="debrief-portal-toggle"
          />
          Visible client
        </label>
      </div>

      {debriefs.length > 0 && (
        <div className="space-y-1.5 pt-1">
          {debriefs.map((d) => (
            <div key={d.id} className="rounded-lg bg-black/30 border border-white/5 p-2.5 space-y-1.5" data-testid={`debrief-${d.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="text-[10px] text-white/50 truncate">
                  <span className="font-mono">{(d.created_at || "").slice(0, 16).replace("T", " ")}</span>
                  <span className="mx-1.5">·</span>
                  <span>{d.user_name}</span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {(isAdmin || d.user_id === user?.id) && (
                    <button onClick={() => togglePortal(d)} className={`text-[9px] px-1.5 py-0.5 rounded-full border flex items-center gap-1 ${d.include_in_portal ? "border-[#005CFF]/50 bg-[#005CFF]/15 text-[#7CB2FF]" : "border-white/10 text-white/40"}`} data-testid={`debrief-portal-${d.id}`} title="Basculer visibilité portail client">
                      {d.include_in_portal ? <Eye size={9} /> : <EyeSlash size={9} />}
                      {d.include_in_portal ? "Publié" : "Interne"}
                    </button>
                  )}
                  {(isAdmin || d.user_id === user?.id) && (
                    <button onClick={() => del(d)} className="text-white/30 hover:text-red-400 p-1" data-testid={`debrief-delete-${d.id}`}>
                      <Trash size={10} />
                    </button>
                  )}
                </div>
              </div>
              {d.transcription ? (
                <div className="text-xs text-white/85 whitespace-pre-wrap leading-relaxed" data-testid={`debrief-transcript-${d.id}`}>{d.transcription}</div>
              ) : (
                <div className="text-[10px] italic text-white/40">Transcription indisponible — écoute uniquement</div>
              )}
              <audio
                controls
                preload="metadata"
                src={`${API}/chantiers/${chantierId}/debrief/${d.id}/audio?auth=${encodeURIComponent(token)}`}
                className="w-full h-7"
                data-testid={`debrief-audio-${d.id}`}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
